import { savePhotoPrivacy } from "@/components/ProfileImageBanner/save-photo-privacy";
import { StickyFooter } from "@/components/StickyFooter";
import { ViewProfile } from "@/components/ViewProfile";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";
import { profilePrompts } from "@iluvatar/global/src/constants/profile-prompts";
import { Button, Dialog, Typography } from "@iluvatar/ui-kit";
import { createFileRoute } from "@tanstack/react-router";
import { extend } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useCallback, useState } from "react";
extend(relativeTime);

import {
  defaultDesireOptions,
  genderOptions,
  MEDIAQUERY,
  pronounOptions,
  sexualityOptions,
} from "@iluvatar/global/src/constants";

import { Form, FormState } from "@/components/Form";
import { RenderFormDefinition } from "@/components/Form/FormDefinitionRender";
import { ProfileImageField } from "@/components/ProfileImageBanner/ProfileImageField";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import DefaultLayout from "@/layout/default-page";
import type { LocalFileRef } from "@/services/file";
import uploadService from "@/services/upload.service";
import { cn } from "@/utilities";
type ProfileFormValues = DeepPartial<UserEntity> & {
  _profileImages?: LocalFileRef[];
  _profileImagePrivacy?: Record<string, boolean>;
};

import {
  HeaderOptions,
  IElementTypes,
  IFieldGroupBuilt,
  IFieldTypes,
  ProfileDetails,
  UserEntity,
} from "@iluvatar/global/src/typings";
import { DeepPartial } from "@iluvatar/global/src/typings/ts-utils";
import { flattenObject, unFlattenObject } from "@iluvatar/global/src/utilities";

import { FormHelpers, useFormContext } from "@/FormLib";
import { formErrorHandler } from "@/components/Form/helpers";

export const Route = createFileRoute("/user/$userId/edit")({
  component: UserProfileSettings,
});

function UserProfileSettings() {
  const params: { userId: string } = Route.useParams();
  const [loading, setLoader] = useState(false);
  const [profileForm, setProfile] = useState<FormState>({
    hasChanges: false,
  });

  const { user, api } = useCurrentUser();
  const hasSubscriptionAccess = useSubscriptionAccess(user?.isSubscribed);

  const onUserUpdate = useCallback(
    async function innerUserHandler(
      userData: ProfileFormValues,
      helpers: FormHelpers<ProfileFormValues>,
    ) {
      try {
        const {
          _profileImages = [],
          _profileImagePrivacy = {},
          ...profileData
        } = userData;
        await api.updateUser(params.userId, profileData);
        if (user) {
          await savePhotoPrivacy(
            params.userId,
            _profileImagePrivacy,
            user.profiles.default,
            api.updateUserImage,
          );
        }
        await uploadService.enqueueAllFormFiles(
          `default-profile-${params.userId}`,
          { _profileImages },
        );
        await helpers.resetForm({
          ...profileData,
          _profileImages: [],
          _profileImagePrivacy: {},
        });
      } catch (error) {
        formErrorHandler(error, helpers);
      }
    },
    [api, params, user],
  );

  const uploadImageOrder = useCallback(
    async (targetIdx: number, sourceIdx: number): Promise<undefined> => {
      if (!user) {
        return;
      }
      try {
        setLoader(true);
        const sourceId = user.profiles.default.imageIds[sourceIdx];
        const newOrder = [
          ...user.profiles.default.imageIds
            .slice(0, targetIdx)
            .filter((id) => id !== sourceId),
          sourceId,
          ...user.profiles.default.imageIds
            .slice(targetIdx)
            .filter((id) => id !== sourceId),
        ];

        await api.updateUser(params.userId, {
          profiles: { default: { imageIds: newOrder } },
        });
      } finally {
        setLoader(false);
      }
      return;
    },
    [api, params.userId, user],
  );

  return (
    <DefaultLayout
      className={cn(
        "profile-container ml-auto mr-auto [max-width:900px] flex flex-col [background:linear-gradient(180deg,_transparent_0%,_transparent_50%,_var(--color-background-default)_50%,_var(--color-background-default))] [@media(min-width:_900px)]:[--page-content-padding:42px] [@media(min-width:_900px)]:[padding-left:42px] [@media(min-width:_900px)]:[padding-right:42px] [&.profile-container--input]:[max-width:900px] [&.profile-container--input]:w-full [@media(max-width:_400px)]:[&.main-container]:[--page-content-padding:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-left:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-right:8px] flex start-start pb-12",
        {
          "pb-[calc(7rem+env(safe-area-inset-bottom))]": profileForm.hasChanges,
        },
      )}
    >
      {user && (
        <Form<ProfileFormValues>
          definitionRenderer={RenderFormDefinition}
          id={`default-profile-${user.uid}`}
          persistLocal
          enableReinitialize
          storagePath={`/users/${user.uid}/upload`}
          initialValues={{
            ...pickOutFormData(user, profileFormDefinition),
            _profileImages: [],
            _profileImagePrivacy: {},
          }}
          formDefinition={{
            ...profileFormDefinition,
            fields: profileFormDefinition.fields?.map((field) =>
              field.name === "profiles.default.incognito"
                ? { ...field, disabled: !hasSubscriptionAccess }
                : field,
            ),
          }}
          setFormMetadata={setProfile}
          onSubmit={onUserUpdate}
          onReset={() => {
            void uploadService.removeTasks(`default-profile-${user.uid}`);
          }}
        >
          <ProfilePreview user={user} />
          <Typography>
            Add photos that show best represent you. Usually thats photos of
            yourself.
          </Typography>
          <ProfileImageField
            isAdmin
            disabled={loading}
            onOrderChange={uploadImageOrder}
            images={
              user.profiles.default.imageIds?.map((id) => ({
                ...(user.profiles.default.imagesById![id] || {}),
                id,
              })) || []
            }
            className={cn("mb-12", { ["opacity-50"]: loading })}
          />
        </Form>
      )}

      {user && profileForm.hasChanges && (
        <StickyFooter>
          <div className="mx-auto flex w-full max-w-[900px] justify-end gap-3 px-4 py-4 min-[900px]:px-[42px]">
            <Button
              type="submit"
              value="reset"
              variant="ghost"
              formNoValidate
              form={`default-profile-${user.uid}`}
            >
              Clear
            </Button>
            <Button
              type="submit"
              variant="outline"
              form={`default-profile-${user.uid}`}
            >
              Save changes
            </Button>
          </div>
        </StickyFooter>
      )}
    </DefaultLayout>
  );
}

function ProfilePreview({ user }: { user: UserEntity }) {
  const isMobile = useMediaQuery(MEDIAQUERY.tablet);
  const { getValues } = useFormContext<ProfileFormValues>();
  const [preview, setPreview] = useState<{
    about: UserEntity["about"];
    profile: ProfileDetails;
  }>();
  const close = () => setPreview(undefined);

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 mt-12 mb-8">
        <Typography
          variant="h2"
          className="text-[var(--color-text--secondary)]"
        >
          Default Profile
        </Typography>
        <Button
          variant="outline"
          onClick={() => {
            const values = getValues();
            setPreview({
              about: { ...user.about, ...values.about } as UserEntity["about"],
              profile: {
                ...user.profiles.default,
                ...values.profiles?.default,
              } as ProfileDetails,
            });
          }}
        >
          Preview
        </Button>
      </div>
      <Dialog
        fullScreen={isMobile}
        open={Boolean(preview)}
        onOpenChange={(next) => {
          if (!next) {
            close();
          }
        }}
        title="Profile preview"
        closeLabel="Close profile preview"
        className={isMobile ? undefined : "!max-w-[900px]"}
      >
        <div className="px-4 min-[780px]:px-6 min-[900px]:px-[42px]">
          {preview && (
            <ViewProfile
              userId={user.uid}
              communityId="default"
              about={preview.about}
              profile={preview.profile}
              preview
            />
          )}
        </div>
        {!isMobile && (
          <div className="mt-6 flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={close}>
              Close
            </Button>
          </div>
        )}
      </Dialog>
    </>
  );
}

const profileFormDefinition: IFieldGroupBuilt = {
  name: "defaultProfile",
  label: "default profile",
  fields: [
    {
      name: "profiles.default.displayName",
      label: "Display Name",
      required: true,
      // defaultValue: user.about.displayName,
      span: 2,
      fieldType: IFieldTypes.textField,
    },
    {
      name: "about.sexuality",
      label: "Sexuality",
      span: 1,
      fieldType: IFieldTypes.autocomplete,
      options: sexualityOptions,
      freeSolo: true,
    },
    {
      name: "about.gender",
      label: "Gender",
      span: 1,
      fieldType: IFieldTypes.autocomplete,
      options: genderOptions,
      freeSolo: true,
    },
    {
      name: "about.pronouns",
      label: "Pronouns",
      hidden: true,
      span: 1,
      fieldType: IFieldTypes.autocomplete,
      options: pronounOptions,
      freeSolo: true,
    },
    {
      name: "profiles.default.description",
      label: "Description",
      required: true,
      span: 4,
      fieldType: IFieldTypes.textArea,
    },
    {
      name: "profiles.default.interests",
      label: "Interests",
      span: 4,
      fieldType: IFieldTypes.asyncAutocompleteField,
      searchInMenu: false,
      disablePopupWhenEmpty: true,
      max: 10,
      multiple: true,
      freeSolo: true,
      freeSoloMaxLength: 20,
    },
    {
      name: "profiles.default.desires",
      label: "Desires",
      span: 4,
      fieldType: IFieldTypes.autocomplete,
      options: defaultDesireOptions,
      ignoreSort: true,
      max: 10,
      multiple: true,
      freeSolo: true,
      freeSoloMaxLength: 20,
    },
    {
      name: "profiles.default.characteristics",
      label: "Characteristics",
      description: `Things you would like others to know about you. (e.g., covid-aware, tall, partnered 
        and poly, mobility impaired). Note, you are posting information about yourself in a public space, 
        be careful not to overshare information a stranger doesn't deserve to know.`,
      span: 4,
      fieldType: IFieldTypes.asyncAutocompleteField,
      searchInMenu: false,
      disablePopupWhenEmpty: true,
      max: 10,
      multiple: true,
      freeSolo: true,
      freeSoloMaxLength: 20,
    },
    {
      fieldType: IElementTypes.header,
      name: "prompts-heading",
      text: "Prompts",
      meta: {
        headerType: HeaderOptions.h2,
        className: "mt-2 mb-4 text-[var(--color-text--secondary)]",
      },
      span: 4,
    },
    ...profilePrompts,
    {
      fieldType: IElementTypes.divider,
      name: "bottom-divider",
      span: 4,
    },
    {
      name: "profiles.default.active",
      description: "Turn off to pauses your account.",
      label: "active",
      span: 1,
      fieldType: IFieldTypes.toggle,
    },
    {
      name: "profiles.default.incognito",
      description:
        "Go incognito for this profiles. You will be able to see and like profiles, but not be seen by others.",
      label: "Incognito",
      disabled: true,
      span: 1,
      fieldType: IFieldTypes.toggle,
    },
  ],
};

function pickOutFormData(
  data: UserEntity,
  form: IFieldGroupBuilt,
): DeepPartial<UserEntity> {
  const flat = flattenObject(data, undefined, { preserveArray: true });
  return unFlattenObject(
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    Object.keys(flat).reduce<any>((acc, key) => {
      if (
        form.fields!.some((f) => {
          return key.startsWith(f.name);
        })
      ) {
        acc[key] = flat[key];
      }
      return acc;
    }, {}),
  ) as DeepPartial<UserEntity>;
}
